/**
 * Put the keyboard on a row a list has just gained.
 *
 * An "Add" button that only appends leaves the author with nothing to see: the
 * new field is wherever the layout puts it, which under a long reference panel
 * is below the fold, and focus stays on the button. Focusing the field says
 * where it went, scrolls it into view, and is where the author was going next.
 *
 * @module components/ui/useFocusOnAdd
 */

import { useCallback, useLayoutEffect, useRef } from "react";

/** Long past a store's late render, so a row arriving after it is not this add's. */
const REQUEST_LIFETIME_MS = 1000;

/**
 * @returns A callback naming the `id` of the field to focus once it renders.
 *
 * @example
 * ```tsx
 * const focusOnAdd = useFocusOnAdd();
 * <Button onClick={() => focusOnAdd(`plo-code-${addOutcome()}`)}>Add outcome</Button>
 * ```
 */
export function useFocusOnAdd(): (elementId: string) => void {
  const pending = useRef<{ elementId: string; expiresAt: number } | null>(null);

  // No dependency list: the render that adds the row is the one that has to be
  // checked, and a store that notifies a render later has to be caught too.
  // The id names one row, so a late match is still the right element.
  useLayoutEffect(() => {
    const request = pending.current;
    if (request === null) {
      return;
    }
    if (Date.now() > request.expiresAt) {
      pending.current = null;
      return;
    }
    const target = document.getElementById(request.elementId);
    if (target) {
      pending.current = null;
      target.focus();
    }
  });

  return useCallback((elementId: string) => {
    pending.current = { elementId, expiresAt: Date.now() + REQUEST_LIFETIME_MS };
  }, []);
}
